import {
  CATEGORY_VALUES,
  COLOR_PALETTE,
  COLOR_VALUES,
  QUIZ_COLOR_TO_FILTER_COLORS,
  QUIZ_RESULTS_SLUG,
  STYLE_OPTIONS_BY_CATEGORY,
  STYLE_VALUES,
} from './constants';
import {
  type CategoryValue,
  type ColorValue,
  type QuizAnswers,
  type QuizOption,
  QuizStep,
  type StyleValue,
} from './types';

import { buildStaticUrl } from '@/constants/endpoints';

const QUIZ_ASSETS_BASE = '/web/assets/quiz/find-your-style';

function getCategoryImageUrl(value: CategoryValue): string {
  return buildStaticUrl(`${QUIZ_ASSETS_BASE}/category-v2/${value}.webp`);
}

function getStyleImageUrl(
  category: CategoryValue,
  styleValue: StyleValue,
  variant: 'desktop' | 'mobile'
): string {
  return buildStaticUrl(
    `${QUIZ_ASSETS_BASE}/style/${category}/${styleValue}-${variant}.jpg`
  );
}

function createImageOptions(
  values: readonly string[],
  keyPrefix: string
): QuizOption[] {
  return values.map((value) => ({
    value,
    labelKey: `FindYourStyle.${keyPrefix}.${value}`,
    type: 'image' as const,
  }));
}

function createColorOptions(values: readonly ColorValue[]): QuizOption[] {
  return values.map((value) => ({
    value,
    labelKey: `FindYourStyle.Color.${value}`,
    type: 'color' as const,
    colors: COLOR_PALETTE[value],
  }));
}

const QUIZ_OPTIONS_BY_STEP: Record<QuizStep, QuizOption[]> = {
  [QuizStep.Category]: createImageOptions(CATEGORY_VALUES, 'Category'),
  [QuizStep.Style]: createImageOptions(STYLE_VALUES, 'Style'),
  [QuizStep.Colors]: createColorOptions(COLOR_VALUES),
};

export function getInitialStep(savedAnswers: QuizAnswers): QuizStep {
  if (savedAnswers.category === null) {
    return QuizStep.Category;
  }
  if (savedAnswers.style === null) {
    return QuizStep.Style;
  }
  return QuizStep.Colors;
}

export function getOptionsForStep(
  step: QuizStep,
  answers: QuizAnswers
): QuizOption[] {
  switch (step) {
    case QuizStep.Category:
      return QUIZ_OPTIONS_BY_STEP[QuizStep.Category].map((option) => ({
        ...option,
        imageSrc: getCategoryImageUrl(option.value as CategoryValue),
      }));

    case QuizStep.Style: {
      const category = (answers.category ?? 'women') as CategoryValue;
      const styleValues = STYLE_OPTIONS_BY_CATEGORY[category];

      return QUIZ_OPTIONS_BY_STEP[QuizStep.Style]
        .filter((option) => styleValues.includes(option.value as StyleValue))
        .map((option) => ({
          ...option,
          imageSrc: getStyleImageUrl(
            category,
            option.value as StyleValue,
            'desktop'
          ),
          imageSrcMobile: getStyleImageUrl(
            category,
            option.value as StyleValue,
            'mobile'
          ),
        }));
    }

    case QuizStep.Colors:
      return QUIZ_OPTIONS_BY_STEP[QuizStep.Colors];
  }
}

export function getResultsURL(answers: QuizAnswers): string {
  const { category, style, colors } = answers;
  if (!category || !style) {
    return '/explore/';
  }
  const slug = QUIZ_RESULTS_SLUG[category]?.[style];
  if (!slug) {
    return '/explore/';
  }

  if (colors) {
    const filterColors = QUIZ_COLOR_TO_FILTER_COLORS[colors];
    if (filterColors?.length) {
      return `/explore/${slug}/?colours=${filterColors.join(',')}`;
    }
  }

  return `/explore/${slug}/`;
}

export function isQuizResultPageSlug(slug: string): boolean {
  return Object.values(QUIZ_RESULTS_SLUG).some((value) =>
    Object.values(value).some((v) => v === slug)
  );
}
